import { useState, useEffect } from "react";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  TrendingUp, Calendar, Target, Megaphone, Globe, 
  Plus, RefreshCw, Edit2, Trash2, ChevronLeft, ChevronRight,
  CheckCircle2, Clock, Rocket, Mail
} from "lucide-react";

const CHANNEL_TYPES = [
  { value: "social", label: "Social Media", icon: Globe },
  { value: "email", label: "Email Campaign", icon: Mail },
  { value: "content", label: "Content", icon: Megaphone },
  { value: "event", label: "Event Marketing", icon: Calendar },
  { value: "partnership", label: "Partnership", icon: Target },
  { value: "ad", label: "Paid Ads", icon: TrendingUp }
];

const STATUS_CONFIG = {
  planned: { label: "Planned", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  live: { label: "Live", color: "bg-green-100 text-green-700", dot: "bg-green-500" },
  completed: { label: "Completed", color: "bg-gray-100 text-gray-700", dot: "bg-gray-500" }
};

export default function MarketingSection() {
  const [activeTab, setActiveTab] = useState("calendar");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => { fetchCampaigns(); }, []);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/marketing/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data.campaigns || []);
      } else {
        setCampaigns(getDefaultCampaigns());
      }
    } catch {
      setCampaigns(getDefaultCampaigns());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultCampaigns = () => [
    { id: "mc1", name: "MUA Pilot Launch", channel: "email", status: "in_progress", start_date: "2026-03-10", end_date: "2026-03-31", description: "Email campaign for MUA pilot invites via Beehiiv" },
    { id: "mc2", name: "Instagram Provider Outreach", channel: "social", status: "planned", start_date: "2026-04-01", end_date: "2026-04-30", description: "DM outreach to LA-based beauty providers" },
    { id: "mc3", name: "Pinterest City Boards", channel: "content", status: "planned", start_date: "2026-04-15", end_date: "2026-05-15", description: "City-specific inspiration boards for SEO" },
    { id: "mc4", name: "HTHF Event Recap", channel: "event", status: "completed", start_date: "2026-02-01", end_date: "2026-02-28", description: "Post-HTHF content and attendee follow-up" },
  ];

  const handleSaveCampaign = async (formData) => {
    try {
      const token = localStorage.getItem("session_token");
      const method = editingCampaign ? "PUT" : "POST";
      const url = editingCampaign 
        ? `${API}/admin/marketing/campaigns/${editingCampaign.id}`
        : `${API}/admin/marketing/campaigns`;
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success(editingCampaign ? "Campaign updated" : "Campaign created");
        setShowDialog(false);
        setEditingCampaign(null);
        fetchCampaigns();
      }
    } catch {
      toast.error("Error saving campaign");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this campaign?")) return;
    try {
      const token = localStorage.getItem("session_token");
      await fetch(`${API}/admin/marketing/campaigns/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign deleted");
      fetchCampaigns();
    } catch {
      toast.error("Error deleting campaign");
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const monthName = currentMonth.toLocaleString("default", { month: "long", year: "numeric" });

  const getCampaignsForDay = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return campaigns.filter(c => {
      if (!c.start_date) return false;
      return dateStr >= c.start_date && dateStr <= (c.end_date || c.start_date);
    });
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));

  if (loading) {
    return <div className="p-8 flex justify-center"><RefreshCw className="w-8 h-8 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="p-4 md:p-8" data-testid="marketing-section">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <TrendingUp className="w-6 h-6" />
            Marketing
          </h1>
          <p className="text-sm text-gray-500">Campaigns, calendar, and outreach management</p>
        </div>
        <Button onClick={() => { setEditingCampaign(null); setShowDialog(true); }} data-testid="add-campaign-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Campaign
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="calendar">Calendar</TabsTrigger>
          <TabsTrigger value="campaigns">All Campaigns</TabsTrigger>
          <TabsTrigger value="channels">Channels</TabsTrigger>
        </TabsList>

        <TabsContent value="calendar" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{monthName}</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>Today</Button>
                  <Button variant="outline" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => (
                  <div key={day} className="bg-gray-50 p-2 text-center text-xs font-medium text-gray-500">
                    {day}
                  </div>
                ))}
                {Array.from({ length: getFirstDayOfMonth(currentMonth) }).map((_, i) => (
                  <div key={`empty-${i}`} className="bg-white p-2 min-h-[80px]" />
                ))}
                {Array.from({ length: getDaysInMonth(currentMonth) }).map((_, i) => {
                  const day = i + 1;
                  const dayCampaigns = getCampaignsForDay(day);
                  const isToday = new Date().getDate() === day && 
                    new Date().getMonth() === currentMonth.getMonth() && 
                    new Date().getFullYear() === currentMonth.getFullYear();
                  return (
                    <div key={day} className={`bg-white p-2 min-h-[80px] ${isToday ? "ring-2 ring-purple-500 ring-inset" : ""}`}>
                      <span className={`text-xs font-medium ${isToday ? "text-purple-600" : "text-gray-600"}`}>{day}</span>
                      <div className="mt-1 space-y-1">
                        {dayCampaigns.slice(0, 2).map(c => (
                          <div 
                            key={c.id} 
                            className={`text-xs px-1 py-0.5 rounded truncate cursor-pointer ${STATUS_CONFIG[c.status]?.color || "bg-gray-100"}`}
                            onClick={() => { setEditingCampaign(c); setShowDialog(true); }}
                            title={c.name}
                          >
                            {c.name}
                          </div>
                        ))}
                        {dayCampaigns.length > 2 && (
                          <span className="text-xs text-gray-400">+{dayCampaigns.length - 2} more</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns" className="mt-6">
          <div className="space-y-4">
            {campaigns.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Megaphone className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">No campaigns yet</p>
                  <p className="text-sm text-gray-500 mt-1">Create your first marketing campaign</p>
                </CardContent>
              </Card>
            ) : (
              campaigns.map(campaign => (
                <Card key={campaign.id} data-testid={`campaign-${campaign.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium">{campaign.name}</h3>
                          <Badge className={STATUS_CONFIG[campaign.status]?.color}>
                            {STATUS_CONFIG[campaign.status]?.label}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {CHANNEL_TYPES.find(c => c.value === campaign.channel)?.label || campaign.channel}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-500">{campaign.description}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {campaign.start_date}  -  {campaign.end_date || "Ongoing"}
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => { setEditingCampaign(campaign); setShowDialog(true); }}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(campaign.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="channels" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CHANNEL_TYPES.map(channel => {
              const channelCampaigns = campaigns.filter(c => c.channel === channel.value);
              const Icon = channel.icon;
              return (
                <Card key={channel.value}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">{channel.label}</p>
                        <p className="text-xs text-gray-500">{channelCampaigns.length} campaigns</p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      {channelCampaigns.slice(0, 3).map(c => (
                        <div key={c.id} className="flex items-center gap-2 text-sm">
                          <div className={`w-2 h-2 rounded-full ${STATUS_CONFIG[c.status]?.dot}`} />
                          <span className="truncate">{c.name}</span>
                        </div>
                      ))}
                      {channelCampaigns.length === 0 && (
                        <p className="text-xs text-gray-400">No campaigns in this channel</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Campaign Dialog */}
      <CampaignDialog 
        isOpen={showDialog} 
        onClose={() => { setShowDialog(false); setEditingCampaign(null); }}
        campaign={editingCampaign}
        onSave={handleSaveCampaign}
      />
    </div>
  );
}

function CampaignDialog({ isOpen, onClose, campaign, onSave }) {
  const [formData, setFormData] = useState({
    name: "", description: "", channel: "social", status: "planned",
    start_date: "", end_date: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (campaign) {
      setFormData({ ...formData, ...campaign });
    } else {
      setFormData({ name: "", description: "", channel: "social", status: "planned", start_date: "", end_date: "" });
    }
  }, [campaign, isOpen]);

  const handleSubmit = async () => {
    if (!formData.name) { toast.error("Campaign name required"); return; }
    setSaving(true);
    await onSave(formData);
    setSaving(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{campaign ? "Edit Campaign" : "New Campaign"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Campaign Name</Label>
            <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="e.g. MUA Launch Campaign" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Channel</Label>
              <Select value={formData.channel} onValueChange={(v) => setFormData(p => ({ ...p, channel: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHANNEL_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData(p => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Start Date</Label>
              <Input type="date" value={formData.start_date} onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value }))} />
            </div>
            <div>
              <Label>End Date</Label>
              <Input type="date" value={formData.end_date} onChange={(e) => setFormData(p => ({ ...p, end_date: e.target.value }))} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
            {campaign ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
